import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function VoteSearchForm({
  initialValue = '',
  placeholder = 'Try "Austin TX" or "Travis County"',
  autoFocus = false,
  compact = false,
}) {
  const navigate = useNavigate();
  const [input, setInput] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (value) => {
    if (value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      // Use backend proxy to avoid CORS issues
      const response = await fetch(
        `/api/geography/address-autocomplete?input=${encodeURIComponent(value)}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API error:', errorData);
        throw new Error(errorData.message || 'Failed to fetch suggestions');
      }

      const data = await response.json();

      if (data.predictions && data.predictions.length > 0) {
        setSuggestions(data.predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleInputChange(e) {
    const value = e.target.value;
    setInput(value);
    fetchSuggestions(value);
  }

  function handleSuggestionClick(suggestion) {
    setInput(suggestion.description);
    setSuggestions([]);
    setShowSuggestions(false);
    const url = suggestion.place_id
      ? `/vote?q=${encodeURIComponent(suggestion.description.trim())}&pid=${encodeURIComponent(suggestion.place_id)}`
      : `/vote?q=${encodeURIComponent(suggestion.description.trim())}`;
    navigate(url);
  }

  function handleSearch(e) {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed.length < 2) return;
    setSuggestions([]);
    setShowSuggestions(false);
    navigate(`/vote?q=${encodeURIComponent(trimmed)}`);
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Enter' && suggestions.length > 0) {
      // Submit with first suggestion on Enter
      e.preventDefault();
      handleSuggestionClick(suggestions[0]);
    }
  }

  function handleInputFocus() {
    if (input.length >= 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }

  function useMyLocation() {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // Use reverse geocoding via the backend to convert coords to address
        fetch(`/api/geography/reverse-geocode?lat=${latitude}&lon=${longitude}`)
          .then((res) => res.json())
          .then((data) => {
            if (data.address) {
              navigate(`/vote?q=${encodeURIComponent(data.address)}`);
            }
          })
          .catch((err) => console.error('Reverse geocoding failed:', err));
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to access your location. Please enable location services.');
      }
    );
  }

  // Get the first suggestion's full text for inline autocomplete display
  const firstSuggestionText = suggestions.length > 0 ? suggestions[0].description : '';
  const autocompleteText = firstSuggestionText.startsWith(input)
    ? firstSuggestionText.slice(input.length)
    : '';

  return (
    <div ref={containerRef} className={`relative ${compact ? 'max-w-full' : 'max-w-xl mx-auto'}`}>
      {/* Use my location button */}
      {!compact && (
        <div className="mb-3 text-center">
          <button
            type="button"
            onClick={useMyLocation}
            style={{
              fontFamily: "'Patrick Hand', cursive",
              fontSize: '15px',
              color: '#fcbf49',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationStyle: 'wavy',
              textUnderlineOffset: '3px',
            }}
          >
            Use my location
          </button>
        </div>
      )}

      <form onSubmit={handleSearch} className={`flex ${compact ? 'gap-1' : 'gap-2'}`}>
        <div className="relative flex-1">
          <div className="relative">
            {/* Autocomplete background text */}
            {autocompleteText && (
              <div className="absolute inset-0 flex items-center px-4 pointer-events-none">
                <span className="text-base opacity-0" style={{ color: '#1a1a1a' }}>
                  {input}
                </span>
                <span className="text-base" style={{ color: '#8a7e6b' }}>
                  {autocompleteText}
                </span>
              </div>
            )}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              placeholder={placeholder}
              autoFocus={autoFocus}
              className={`relative w-full focus:outline-none ${
                compact ? 'px-3 py-2 text-sm' : 'px-4 py-3 text-base'
              }`}
              style={{
                fontFamily: "'Patrick Hand', cursive",
                background: '#fefcf6',
                color: '#1a1a1a',
                border: '2px solid #fefcf6',
                borderRadius: 0,
                boxShadow: '3px 3px 0px rgba(0,0,0,0.3)',
              }}
              onFocusCapture={(e) => {
                e.currentTarget.style.border = '2px solid #d62828';
                e.currentTarget.style.boxShadow = '3px 3px 0px #d62828';
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.border = '2px solid #fefcf6';
                e.currentTarget.style.boxShadow = '3px 3px 0px rgba(0,0,0,0.3)';
              }}
            />
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute top-full left-0 right-0 mt-1 z-10"
              style={{
                background: '#fefcf6',
                border: '2px solid #1a1a1a',
                boxShadow: '3px 3px 0px #1a1a1a',
              }}
            >
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left px-4 py-2 transition-colors"
                  style={{
                    fontFamily: "'Patrick Hand', cursive",
                    color: '#1a1a1a',
                    borderBottom: idx < suggestions.length - 1 ? '1px dashed #bfb49e' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ede5d4';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div className="font-bold text-sm">
                    {suggestion.main_text || suggestion.description.split(',')[0]}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#8a7e6b',
                      fontFamily: "'Special Elite', monospace",
                    }}
                  >
                    {suggestion.secondary_text ||
                      suggestion.description.split(',').slice(1).join(',')}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Loading indicator */}
          {loading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div
                className="w-4 h-4 border-2 rounded-full animate-spin"
                style={{ borderColor: '#bfb49e', borderTopColor: '#d62828' }}
              />
            </div>
          )}
        </div>

        <button
          type="submit"
          style={{
            fontFamily: "'Permanent Marker', cursive",
            fontSize: compact ? '14px' : '16px',
            color: '#fefcf6',
            background: '#d62828',
            border: '2px solid #fefcf6',
            borderRadius: 0,
            padding: compact ? '8px 12px' : '12px 24px',
            cursor: 'pointer',
            boxShadow: '3px 3px 0px rgba(0,0,0,0.3)',
            transition: 'all 0.1s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translate(-1px, -1px)';
            e.currentTarget.style.boxShadow = '4px 4px 0px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '3px 3px 0px rgba(0,0,0,0.3)';
          }}
        >
          {compact ? 'GO' : 'SEARCH'}
        </button>
      </form>
    </div>
  );
}
